/* Tienda Novale – <product-form>.
   Simple forms (product cards, quick add) add the selected variant.
   Main product forms also add what the page attaches to the product: quantity breaks (with one variant per unit),
   selected upsells, unlocked quantity gifts, the "main" bundle offer and custom fields.
   Bundle forms (data-bundle) add the bundle products only. */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        if (!this.form) return;
        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        this.form.addEventListener('submit', (event) => this.onSubmitHandler(event));
        if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.querySelectorAll('[data-skip-cart-button]').forEach((button) =>
          button.addEventListener('click', (event) => this.onSubmitHandler(event, { skipCart: true, button }))
        );
      }

      get submitButton() {
        return this.querySelector('button[name="add"]');
      }

      get scope() {
        return (
          this.closest('.quick-add-modal__content-info') ||
          document.getElementById(`shopify-section-${this.dataset.section}`) ||
          document
        );
      }

      get isMain() {
        return this.dataset.main === 'true';
      }

      async onSubmitHandler(event, options = {}) {
        if (event) event.preventDefault();
        const button = options.button || this.submitButton;
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        if (window.TN && TN.fields && !TN.fields.validate(this.scope, true)) return;

        this.handleErrorMessage();
        this.setLoading(button, true);
        const skipCart = options.skipCart || this.dataset.skipCart === 'true';
        const items = this.buildItems();

        try {
          if (skipCart) {
            await this.post({ items }, false);
            window.location.href = '/checkout';
            return;
          }
          const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cartEl && cartEl.setActiveElement) cartEl.setActiveElement(document.activeElement);
          await this.addToCart(items);
          const modal = this.closest('quick-add-modal');
          if (modal) {
            document.body.addEventListener('modalClosed', () => setTimeout(() => this.openCart(), 50), { once: true });
            modal.hide(true);
          }
        } catch (error) {
          this.handleErrorMessage(this.errorText(error));
        } finally {
          this.setLoading(button, false);
        }
      }

      setLoading(button, loading) {
        const spinner = button.querySelector('.loading-overlay__spinner');
        button.classList.toggle('loading', loading);
        if (loading) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.toggle('hidden', !loading);
      }

      errorText(error) {
        if (!error) return window.cartStrings ? window.cartStrings.error : 'Error';
        if (typeof error.description === 'string' && error.description) return error.description;
        if (typeof error.message === 'string' && error.message) return error.message;
        return window.cartStrings ? window.cartStrings.error : 'Error';
      }

      /* ---------- Items ---------- */
      buildItems() {
        const data = new FormData(this.form);
        const properties = {};
        for (const [key, value] of data.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        }
        const sellingPlan = data.get('selling_plan');
        const mainId = Number(data.get('id'));
        const scope = this.scope;
        const items = [];
        const push = (id, quantity, extra) => {
          id = Number(id);
          if (!id || quantity <= 0) return;
          const same = items.find((item) => item.id === id && !item.properties && !extra);
          if (same) same.quantity += quantity;
          else items.push(Object.assign({ id, quantity }, extra || {}));
        };

        /* Bundle with its own button */
        if (this.dataset.bundle) {
          const bundle = document.querySelector(this.dataset.bundle);
          if (bundle) {
            bundle.querySelectorAll('.bundle-offer__item[data-variant-id]').forEach((item) => push(item.dataset.variantId, 1));
            if (bundle.dataset.includeUpsells === 'true') this.upsellItems(scope).forEach((id) => push(id, 1));
          }
          return items;
        }

        if (!this.isMain) {
          const quantity = parseInt(data.get('quantity'), 10) || 1;
          push(mainId, quantity, this.mainExtra(properties, sellingPlan));
          return items;
        }

        this.collectFieldProperties(scope, properties);
        const extra = this.mainExtra(properties, sellingPlan);
        const quantity = this.selectedQuantity(scope, data);
        const bundle = scope.querySelector('tn-bundle[data-type="main"]');

        if (bundle) {
          bundle.querySelectorAll('.bundle-offer__item[data-variant-id]').forEach((item) => push(item.dataset.variantId, quantity));
        } else {
          const units = this.unitVariants(scope);
          if (units) units.forEach((id) => push(id, 1, extra ? JSON.parse(JSON.stringify(extra)) : null));
          else push(mainId, quantity, extra);
        }
        this.upsellItems(scope).forEach((id) => push(id, 1));
        scope.querySelectorAll('quantity-gifts .quantity-gift[data-unlocked="true"][data-variant-id]').forEach((gift) => push(gift.dataset.variantId, 1));

        /* Units with the same variant and properties are merged */
        if (extra) {
          const merged = [];
          items.forEach((item) => {
            const same = merged.find((m) => m.id === item.id && JSON.stringify(m.properties || null) === JSON.stringify(item.properties || null) && m.selling_plan === item.selling_plan);
            if (same) same.quantity += item.quantity;
            else merged.push(item);
          });
          return merged;
        }
        return items;
      }

      mainExtra(properties, sellingPlan) {
        const extra = {};
        if (Object.keys(properties).length) extra.properties = properties;
        if (sellingPlan) extra.selling_plan = Number(sellingPlan);
        return Object.keys(extra).length ? extra : null;
      }

      collectFieldProperties(scope, properties) {
        scope.querySelectorAll('tn-custom-field').forEach((field) => {
          const value = field.value;
          if (value !== '' && field.dataset.name) properties[field.dataset.name] = value;
        });
      }

      selectedQuantity(scope, data) {
        const breaks = scope.querySelector('quantity-breaks');
        if (breaks) {
          const checked = breaks.querySelector('.quantity-breaks__input:checked');
          if (checked) return parseInt(checked.value, 10) || 1;
        }
        return parseInt(data.get('quantity'), 10) || 1;
      }

      /* Variant ids of each unit when the selected quantity break has one picker per unit. */
      unitVariants(scope) {
        const breaks = scope.querySelector('quantity-breaks');
        if (!breaks || typeof breaks.unitVariantIds !== 'function') return null;
        return breaks.unitVariantIds();
      }

      upsellItems(scope) {
        return Array.from(scope.querySelectorAll('tn-upsell[data-context="product"][data-toggle="true"][data-selected="true"]'))
          .map((upsell) => upsell.dataset.variantId)
          .filter(Boolean);
      }

      /* ---------- Cart ---------- */
      async post(body, withSections = true) {
        const cartEl = document.querySelector('cart-notification');
        if (withSections && cartEl) {
          body.sections = cartEl.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw data;
        return data;
      }

      async addToCart(items) {
        const drawer = document.querySelector('cart-drawer');
        const notification = document.querySelector('cart-notification');
        const inModal = !!this.closest('quick-add-modal');

        if (drawer && window.TN && TN.cart) {
          await TN.cart.add(items, { open: !inModal });
          this.pendingOpen = inModal;
          return;
        }

        const data = await this.post({ items });
        if (!drawer && !notification) {
          window.location = window.routes.cart_url;
          return;
        }
        if (notification) {
          data.key = data.items && data.items.length ? data.items[0].key : data.key;
          if (inModal) this.pendingNotification = data;
          else notification.renderContents(data);
        }
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
      }

      openCart() {
        const drawer = document.querySelector('cart-drawer');
        const notification = document.querySelector('cart-notification');
        if (this.pendingOpen && drawer) drawer.open();
        if (this.pendingNotification && notification) notification.renderContents(this.pendingNotification);
        this.pendingOpen = false;
        this.pendingNotification = null;
      }

      /* ---------- Errors ---------- */
      handleErrorMessage(message = false) {
        this.errorWrapper = this.errorWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (this.errorWrapper) {
          this.errorWrapper.toggleAttribute('hidden', !message);
          const text = this.errorWrapper.querySelector('.product-form__error-message');
          if (text && message) text.textContent = message;
        }
        if (message && this.isMain) {
          const sticky = document.getElementById(`StickyAtc-${this.dataset.section}`);
          if (sticky && typeof sticky.flashError === 'function') sticky.flashError(message);
          else if (this.closest('.product-buy-buttons.hidden')) window.alert(message);
        }
      }
    }
  );
}
